import React, { useContext, useMemo } from 'react';
import _ from 'lodash';

import useSlideoutBonusApp from 'components/PerformanceBoards/hooks/useSlideoutBonusApp';

import PerformanceBoardSlideOutContext from '../../PerformanceBoards/contexts/PerformanceBoardSlideOutContext';
import SelectedEntityContext from '../../PerformanceBoards/contexts/SelectedEntityContext';
import sectionTypeCheckers from '../../PerformanceBoards/PerformanceBoardSlideOut/sectionTypeCheckers';
import isDefined from '../../../isDefined';
import BonusOverviewProvider from './BonusOverviewProvider';
import WeeklyBonusOverrideProvider from './WeeklyBonusOverrideProvider';

/**
 * Injects a BonusOverviewContext into the slideout when it's needed. Two cases:
 *
 * 1. The slideout has bonus reward/total-reward sections configured — the
 *    context feeds those sections (existing behaviour). It throws if multiple
 *    bonus programs are found across those sections as that isn't supported.
 * 2. The board's `isSlideoutOverrideEnabled` setting is on (and its Override
 *    button isn't hidden via `isSlideoutOverrideButtonEnabled`) and the entity's app
 *    is a bonus app — the context powers the dedicated in-slideout override
 *    workflow even when no bonus sections are configured. The program id comes
 *    from the app's `driver-bonus-program-report` scoring tab.
 */
const BoardBonusOverviewProvider = ({
  board,
  children,
}: {
  board: PerformanceBoardTypes.Board;
  children: JSX.Element | JSX.Element[];
}) => {
  const { selectedEntity, onEntityUnavailable } = useContext(
    SelectedEntityContext,
  );
  const { refreshBoard } = useContext(PerformanceBoardSlideOutContext);
  // With the Override button hidden there's no way into the dedicated workflow,
  // so don't mount (and pre-fetch for) it.
  const isSlideoutOverrideEnabled =
    board.isSlideoutOverrideButtonEnabled !== false &&
    !!board.isSlideoutOverrideEnabled;
  const { isBonusApp, bonusProgramId: appBonusProgramId } =
    useSlideoutBonusApp();

  const hasBonusContent = useMemo(() => {
    return board.slideout.some((item) => {
      return (
        sectionTypeCheckers.isBonusRewardSection(item) ||
        sectionTypeCheckers.isBonusTotalRewardSection(item) ||
        sectionTypeCheckers.isRecognitionPaymentsSection(item)
      );
    });
  }, [board.slideout]);

  const sectionBonusProgramId = useMemo(() => {
    if (!hasBonusContent) {
      return undefined;
    }

    const rewardIds = _.uniq(
      board.slideout
        .map((s) => {
          if (
            sectionTypeCheckers.isBonusTotalRewardSection(s) ||
            sectionTypeCheckers.isBonusRewardSection(s) ||
            sectionTypeCheckers.isRecognitionPaymentsSection(s)
          ) {
            return s.bonusConfigId;
          }
          return undefined;
        })
        .filter(isDefined),
    );

    if (rewardIds.length === 1) {
      // Only one reward program is supported
      return rewardIds[0];
    }

    if (rewardIds.length > 1) {
      const e = new Error();
      e.name = 'BoardBonusOverviewProvider: Multiple programs found';
      throw e;
    }

    return undefined;
  }, [board.slideout, hasBonusContent]);

  if (hasBonusContent) {
    if (!selectedEntity || !sectionBonusProgramId) {
      return null;
    }

    return (
      <BonusOverviewProvider
        bonusProgramId={sectionBonusProgramId}
        driver={selectedEntity}
        variant={'slideout'}
        onOverridesSaved={refreshBoard}
        onRewardsUnavailable={onEntityUnavailable}
      >
        <WeeklyBonusOverrideProvider
          driver={selectedEntity}
          bonusProgramId={sectionBonusProgramId}
          onOverridesSaved={refreshBoard}
        >
          {children}
        </WeeklyBonusOverrideProvider>
      </BonusOverviewProvider>
    );
  }

  // Dedicated in-slideout override workflow: mount the context for any bonus app
  // even without configured bonus sections, so the Override button and the
  // override view share one provider instance. Gated by the board setting so
  // boards with it off keep the navigate-away popup behaviour untouched.
  if (
    isSlideoutOverrideEnabled &&
    isBonusApp &&
    selectedEntity &&
    appBonusProgramId
  ) {
    return (
      <BonusOverviewProvider
        bonusProgramId={appBonusProgramId}
        driver={selectedEntity}
        variant={'slideout'}
        onOverridesSaved={refreshBoard}
      >
        {children}
      </BonusOverviewProvider>
    );
  }

  return <>{children}</>;
};

export default BoardBonusOverviewProvider;
